import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, GripVertical, Building2, Star, AlertTriangle, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ClientTypeRecord } from "@shared/schema";
import { QualificationProfileSection } from "./qualification-profile-section";

interface AdminUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profile?: {
    id: string;
    userId: string;
    role: string;
    allowedClientTypeIds?: string[] | null;
  };
}

function userDisplayName(u: AdminUser): string {
  const fn = (u.firstName || "").trim();
  const ln = (u.lastName || "").trim();
  const full = [fn, ln].filter(Boolean).join(" ");
  return full || u.email || u.id;
}

function VisibleToUsersCell({ clientTypeId }: { clientTypeId: string }) {
  const { toast } = useToast();
  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ["/api/admin/users"],
  });

  const mutation = useMutation({
    mutationFn: async ({ userId, allowedClientTypeIds }: { userId: string; allowedClientTypeIds: string[] }) => {
      return await apiRequest("PATCH", `/api/admin/users/${userId}/client-type-access`, { allowedClientTypeIds });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] }),
    onError: (err: any) => toast({ title: "Failed to update", description: err?.message || "Could not update visibility.", variant: "destructive" }),
  });

  const scopedUsers = users.filter((u) => {
    const role = u.profile?.role;
    return role !== "admin" && role !== "billing";
  });

  const visibleCount = scopedUsers.filter((u) => {
    const allowed = u.profile?.allowedClientTypeIds || [];
    return allowed.length === 0 || allowed.includes(clientTypeId);
  }).length;

  const totalUsers = scopedUsers.length;

  const toggle = (user: AdminUser) => {
    const current = user.profile?.allowedClientTypeIds || [];
    if (current.length === 0) return;
    const next = current.includes(clientTypeId)
      ? current.filter((x) => x !== clientTypeId)
      : [...current, clientTypeId];
    mutation.mutate({ userId: user.id, allowedClientTypeIds: next });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-xs"
          data-testid={`button-visibility-${clientTypeId}`}
        >
          <Users className="w-3 h-3" />
          Visible to {visibleCount}/{totalUsers}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <p className="text-sm font-medium">Per-user visibility</p>
          <p className="text-xs text-muted-foreground">
            Admins and Billing always see every type. Users with no restrictions see everything.
          </p>
        </div>
        <ScrollArea className="max-h-72">
          <div className="p-2 space-y-1">
            {scopedUsers.length === 0 && (
              <p className="text-xs text-muted-foreground px-2 py-3">No non-admin users.</p>
            )}
            {scopedUsers.map((u) => {
              const allowed = u.profile?.allowedClientTypeIds || [];
              const isUnrestricted = allowed.length === 0;
              const checked = isUnrestricted || allowed.includes(clientTypeId);
              return (
                <label
                  key={u.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm hover-elevate ${isUnrestricted ? "opacity-70" : "cursor-pointer"}`}
                  data-testid={`row-visibility-user-${u.id}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isUnrestricted || mutation.isPending}
                    onCheckedChange={() => toggle(u)}
                    data-testid={`checkbox-visibility-user-${u.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{userDisplayName(u)}</div>
                    {isUnrestricted && (
                      <div className="text-[10px] text-muted-foreground">Unrestricted (sees all)</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function SortableClientTypeRow({
  id,
  className,
  renderRow,
}: {
  id: string;
  className?: string;
  renderRow: (handle: React.ReactNode) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };
  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="touch-none cursor-grab active:cursor-grabbing flex-shrink-0 p-0.5 -ml-0.5 rounded hover:bg-muted text-muted-foreground"
      aria-label="Drag to reorder"
      data-testid={`drag-handle-client-type-${id}`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style}>
      <Card className={className} data-testid={`row-client-type-${id}`}>
        {renderRow(handle)}
      </Card>
    </div>
  );
}

interface ManageClientTypesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

interface RecordCounts {
  leads: number;
  clients: number;
  invoices: number;
}

export function ManageClientTypesDialog({ open, onOpenChange, inPanel = false }: ManageClientTypesDialogProps) {
  const { toast } = useToast();
  const [editingClientType, setEditingClientType] = useState<ClientTypeRecord | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    isActive: true,
    sortOrder: 0,
  });
  
  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<ClientTypeRecord | null>(null);
  const [recordCounts, setRecordCounts] = useState<RecordCounts | null>(null);
  const [reassignToType, setReassignToType] = useState<string>("");
  const [isCreatingNewType, setIsCreatingNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  const { data: clientTypes = [], isLoading } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  // Local order state so drag-reorder feels instant and doesn't fight react-query.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  useEffect(() => {
    setOrderedIds(clientTypes.map((ct) => ct.id));
  }, [clientTypes]);
  const orderedClientTypes = orderedIds
    .map((id) => clientTypes.find((ct) => ct.id === id))
    .filter((ct): ct is ClientTypeRecord => !!ct);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderMutation = useMutation<void, any, string[], { previous: string[] }>({
    mutationFn: async (ids: string[]) => {
      await apiRequest("POST", "/api/client-types/reorder", { orderedIds: ids });
    },
    onMutate: async (ids) => {
      // Snapshot the order at mutation time so rollback uses the right baseline.
      const previous = orderedIds;
      setOrderedIds(ids);
      return { previous };
    },
    onError: (error: any, _ids, context) => {
      if (context?.previous) {
        setOrderedIds(context.previous);
      }
      toast({ title: "Reorder failed", description: error?.message || "Could not save the new order.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Serialize: drop drags while a save is in flight to avoid racing rollbacks.
    if (reorderMutation.isPending) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    reorderMutation.mutate(next);
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", "/api/client-types", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      toast({ title: "Client Type Created", description: "The new client type has been added." });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create client type.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const response = await apiRequest("PATCH", `/api/client-types/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      toast({ title: "Client Type Updated", description: "The client type has been updated." });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update client type.", variant: "destructive" });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/client-types/${id}/set-default`, {});
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/client-types/default"] });
      toast({ title: "Default Set", description: "The default client type has been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set default client type.", variant: "destructive" });
    },
  });

  const clearDefaultMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/client-types/clear-default", {});
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/client-types/default"] });
      toast({ title: "Default Set to All", description: "The filter will now default to showing all client types." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update default.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/client-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      toast({ title: "Client Type Deleted", description: "The client type has been removed." });
      closeDeleteDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete client type.", variant: "destructive" });
    },
  });

  const reassignAndDeleteMutation = useMutation({
    mutationFn: async ({ id, newClientType }: { id: string; newClientType: string }) => {
      const response = await apiRequest("POST", `/api/client-types/${id}/reassign-and-delete`, { newClientType });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      const { reassigned } = data;
      toast({ 
        title: "Records Reassigned & Type Deleted", 
        description: `Moved ${reassigned.leads} leads, ${reassigned.clients} clients, and ${reassigned.invoices} invoices.`
      });
      closeDeleteDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to reassign records.", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setEditingClientType(null);
    setIsCreating(false);
    setFormData({
      name: "",
      isActive: true,
      sortOrder: clientTypes.length,
    });
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setRecordCounts(null);
    setReassignToType("");
    setIsCreatingNewType(false);
    setNewTypeName("");
  };

  const handleEdit = (clientType: ClientTypeRecord) => {
    setEditingClientType(clientType);
    setIsCreating(false);
    setFormData({
      name: clientType.name,
      isActive: clientType.isActive ?? true,
      sortOrder: clientType.sortOrder ?? 0,
    });
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Client type name is required.", variant: "destructive" });
      return;
    }

    if (editingClientType) {
      updateMutation.mutate({ id: editingClientType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteClick = async (clientType: ClientTypeRecord) => {
    setDeleteTarget(clientType);
    
    // Fetch record counts
    try {
      const response = await apiRequest("GET", `/api/client-types/${clientType.id}/record-counts`);
      const counts = await response.json();
      setRecordCounts(counts);
    } catch (error) {
      console.error("Error fetching record counts:", error);
      setRecordCounts({ leads: 0, clients: 0, invoices: 0 });
    }
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    // Include all record types: leads, clients, and invoices (via clients)
    const totalRecords = (recordCounts?.leads || 0) + (recordCounts?.clients || 0) + (recordCounts?.invoices || 0);
    
    if (totalRecords > 0) {
      // Has records - need to reassign
      const targetType = isCreatingNewType ? newTypeName : reassignToType;
      if (!targetType) {
        toast({ title: "Error", description: "Please select or create a client type to reassign records to.", variant: "destructive" });
        return;
      }
      
      if (isCreatingNewType) {
        // First create the new type, then reassign and delete
        createMutation.mutate({ name: newTypeName, isActive: true, sortOrder: clientTypes.length }, {
          onSuccess: () => {
            reassignAndDeleteMutation.mutate({ id: deleteTarget.id, newClientType: newTypeName });
          }
        });
      } else {
        reassignAndDeleteMutation.mutate({ id: deleteTarget.id, newClientType: targetType });
      }
    } else {
      // No records - safe to delete
      deleteMutation.mutate(deleteTarget.id);
    }
  };

  const handleSetDefault = (clientType: ClientTypeRecord) => {
    setDefaultMutation.mutate(clientType.id);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  // "All" is the default when no specific client type has isDefault=true
  const isAllDefault = clientTypes.length > 0 && clientTypes.every(ct => !ct.isDefault);
  const isDeleting = deleteMutation.isPending || reassignAndDeleteMutation.isPending;

  const hasRecords = (recordCounts?.leads || 0) + (recordCounts?.clients || 0) + (recordCounts?.invoices || 0) > 0;
  const availableTypesForReassign = clientTypes.filter(ct => ct.id !== deleteTarget?.id);

  const innerContent = (<>
          {!inPanel && (
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Manage Client Types
              </DialogTitle>
              <DialogDescription>
                Configure client types used across leads, clients, and invoices. Set one as default for new records.
              </DialogDescription>
            </DialogHeader>
          )}

          <div className="space-y-4">
            {!isCreating && !editingClientType && (
              <Button 
                onClick={() => {
                  setIsCreating(true);
                  setFormData({ name: "", isActive: true, sortOrder: clientTypes.length });
                }}
                className="w-full"
                data-testid="button-add-client-type"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Client Type
              </Button>
            )}

            {(isCreating || editingClientType) && (
              <Card>
                <CardContent className="pt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Schools, Corporate, Private"
                      data-testid="input-client-type-name"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="isActive">Active</Label>
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                      data-testid="switch-client-type-active"
                    />
                  </div>

                  {editingClientType && (
                    <QualificationProfileSection clientTypeId={editingClientType.id} clientTypeName={editingClientType.name} />
                  )}

                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={resetForm} data-testid="button-cancel-client-type">
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSubmit} 
                      disabled={isSubmitting}
                      data-testid="button-save-client-type"
                    >
                      {isSubmitting ? "Saving..." : editingClientType ? "Update" : "Create"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Existing Client Types</h3>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : clientTypes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No client types configured yet.</p>
              ) : (
                <div className="space-y-2">
                  {/* "All Client Types" virtual row — star it to make the filter default to showing all types */}
                  <Card className="border-dashed">
                    <CardContent className="py-3 px-4 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <GripVertical className="w-4 h-4 text-muted-foreground/30 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-muted-foreground">All Client Types</span>
                            {isAllDefault && (
                              <Badge variant="default" className="text-xs gap-1">
                                <Star className="w-3 h-3" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">Filter defaults to showing all types</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!isAllDefault && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => clearDefaultMutation.mutate()}
                            disabled={clearDefaultMutation.isPending}
                            title="Set 'All' as default filter"
                            data-testid="button-set-default-all"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                  {orderedClientTypes.map((clientType, idx) => (
                    <SortableClientTypeRow
                      key={clientType.id}
                      id={clientType.id}
                      className={!clientType.isActive ? "opacity-50" : ""}
                      renderRow={(handle) => (
                      <CardContent className="py-3 px-4 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {handle}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate" data-testid={`text-client-type-name-${clientType.id}`}>
                                {clientType.name}
                              </span>
                              {clientType.isDefault && (
                                <Badge variant="default" className="text-xs gap-1">
                                  <Star className="w-3 h-3" />
                                  Default
                                </Badge>
                              )}
                              {!clientType.isActive && (
                                <Badge variant="secondary" className="text-xs">Inactive</Badge>
                              )}

                            </div>
                            <p className="text-xs text-muted-foreground">
                              Order: {idx + 1}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <VisibleToUsersCell clientTypeId={clientType.id} />
                          {!clientType.isDefault && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleSetDefault(clientType)}
                              disabled={setDefaultMutation.isPending}
                              title="Set as Default"
                              data-testid={`button-set-default-${clientType.id}`}
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEdit(clientType)}
                            data-testid={`button-edit-client-type-${clientType.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteClick(clientType)}
                            disabled={deleteMutation.isPending || clientType.isDefault}
                            title={clientType.isDefault ? "Cannot delete default type" : "Delete"}
                            data-testid={`button-delete-client-type-${clientType.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                      )}
                    />
                  ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}
            </div>
          </div>
    </>
  );

  const childDialogs = (
    <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && closeDeleteDialog()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Delete Client Type
          </DialogTitle>
          <DialogDescription>
            {hasRecords ? (
              <>
                You are deleting "<strong>{deleteTarget?.name}</strong>" which has associated records:
                <ul className="mt-2 list-disc list-inside text-sm">
                  <li>{recordCounts?.leads || 0} leads</li>
                  <li>{recordCounts?.clients || 0} clients</li>
                  <li>{recordCounts?.invoices || 0} invoices (via clients)</li>
                </ul>
                <p className="mt-2">Please select a client type to reassign these records to:</p>
              </>
            ) : (
              <>
                Are you sure you want to delete "<strong>{deleteTarget?.name}</strong>"?
                {recordCounts && <span className="block mt-1 text-sm">This type has no associated records.</span>}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {hasRecords && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reassign records to:</Label>
              {!isCreatingNewType ? (
                <div className="space-y-2">
                  <Select
                    value={reassignToType}
                    onValueChange={setReassignToType}
                  >
                    <SelectTrigger data-testid="select-reassign-type">
                      <SelectValue placeholder="Select a client type" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTypesForReassign.map((ct) => (
                        <SelectItem key={ct.id} value={ct.name}>
                          {ct.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setIsCreatingNewType(true)}
                    className="p-0 h-auto"
                    data-testid="button-create-new-type"
                  >
                    Or create a new client type
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    placeholder="Enter new client type name"
                    data-testid="input-new-type-name"
                  />
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                      setIsCreatingNewType(false);
                      setNewTypeName("");
                    }}
                    className="p-0 h-auto"
                  >
                    Select existing type instead
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            disabled={isDeleting || (hasRecords && !reassignToType && !newTypeName)}
            data-testid="button-confirm-delete"
          >
            {isDeleting ? "Processing..." : hasRecords ? "Reassign & Delete" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (inPanel) {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
          {innerContent}
        </div>
        {childDialogs}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {innerContent}
        </DialogContent>
      </Dialog>
      {childDialogs}
    </>
  );
}
